/**
 * Product rules make products unavailable for selection when their conditions
 * match the order's vehicle or the customer's account group. They are
 * deny-only: a rule can hide products, never reveal them, and it blocks only
 * when *every* condition actively matches.
 *
 * Mirrors src/product-rules/ in the api-engine-program backend.
 */

export enum ProductRuleField {
  make = "make",
  model = "model",
  gearbox = "gearbox",
  engine = "engine",
  ecu = "ecu",
  accountGroup = "accountGroup",
}

export enum ProductRuleOperator {
  in = "in",
  notIn = "not_in",
  contains = "contains",
}

export interface IProductRuleCondition {
  field: ProductRuleField;
  operator: ProductRuleOperator;
  /** For `accountGroup` these are account group ids as strings. */
  values: string[];
}

export interface IProductRule {
  id: number | string;
  name: string;
  description?: string | null;
  /** Reason shown to the customer when this rule blocks a product. */
  message?: string | null;
  isActive: boolean;
  conditions: IProductRuleCondition[];
  /** The API returns hydrated products but accepts numeric ids on write. */
  products?: Array<{ id: number | string; name?: string; code?: string }>;
  productIds?: Array<number | string>;
  tenantId?: number;
  createdAt?: string;
  updatedAt?: string;
  /** Client-only flag for a card that has never been saved. */
  _isNew?: boolean;
}

/* -------------------------------------------------------------------------- */
/* GET /product-rules/metadata                                                 */
/* -------------------------------------------------------------------------- */

export interface IProductRuleFieldOption {
  value: string;
  label: string;
}

export interface IProductRuleFieldMetadata {
  key: ProductRuleField;
  label: string;
  operators: ProductRuleOperator[];
  /** "text" renders a creatable combobox, "reference" a closed select. */
  valueType: "text" | "reference";
  suggestions?: string[];
  options?: IProductRuleFieldOption[];
  dependsOn?: ProductRuleField;
  suggestionsEndpoint?: string;
}

export interface IProductRuleMetadata {
  fields: IProductRuleFieldMetadata[];
  operators: Array<{ key: ProductRuleOperator; label: string }>;
}

/* -------------------------------------------------------------------------- */
/* POST /product-rules/test                                                    */
/* -------------------------------------------------------------------------- */

export interface IProductRuleTestContext {
  accountId?: number;
  make?: string;
  model?: string;
  gearbox?: string;
  engine?: string;
  ecu?: string;
}

export interface IProductRuleDraft {
  /** Saved rule this draft stands in for; that rule is excluded from the run. */
  ruleId?: number;
  name?: string;
  message?: string | null;
  productIds: number[];
  conditions: IProductRuleCondition[];
}

export interface IMatchedCondition {
  field: ProductRuleField;
  operator: ProductRuleOperator;
  values: string[];
  /** The value the condition was actually compared against. */
  contextValue: string | null;
  matched: boolean;
}

export interface IBlockingRule {
  ruleId: number;
  ruleName: string;
  message: string | null;
  isDraft: boolean;
  matchedConditions: IMatchedCondition[];
}

export interface IProductAvailability {
  id: number;
  code: string;
  name: string;
  available: boolean;
  blockedBy: IBlockingRule | null;
}

export interface IResolvedContext {
  make: string | null;
  model: string | null;
  gearbox: string | null;
  engine: string | null;
  ecu: string | null;
  accountId: number | null;
  accountGroup: { id: number; name: string | null } | null;
}

export interface IProductRuleTestResult {
  resolvedContext: IResolvedContext;
  products: IProductAvailability[];
  summary: { total: number; available: number; blocked: number };
}
